/*
 *** Usage in FO: OK
 *** Usage in PO: OK
 *** Usage in BO: OK
 */

import PropTypes from 'prop-types';
import { forwardRef, useCallback, useState } from 'react';
import styled, { css } from 'styled-components';

import { Box } from '@hivebrite/design-system/Box';
import { IconArrowDownHollow } from '@hivebrite/design-system/Icon';
import { focusOutline } from '@hivebrite/design-system/Theme';

import Required from 'hb-react/shared/components/Required';
import IconExclamationMarkHollow from 'hb-react/shared/icons/ExclamationMarkHollow';

const ICON_SIZE = 20;

/**
 * @deprecated please use `import { ReactSelectInput } from '@hivebrite/design-system/Inputs'` instead
 */
const Select = forwardRef(
  (
    {
      className,
      dataTestId,
      disabled,
      error,
      fontSize,
      height,
      hideLabel,
      iconSpaceRight,
      id,
      label,
      lineHeight = 1.2,
      onBlur,
      onChange,
      onFocus,
      options = [],
      required,
      size = 'normal',
      type = 'text',
      value,
      width,
    },
    ref,
  ) => {
    const [isFocus, setIsFocus] = useState(false);

    const hasError = !!error;
    const errorId = `${id}-error`;

    const handleFocus = useCallback(() => {
      setIsFocus(true);

      if (onFocus) {
        onFocus();
      }
    }, [onFocus]);
    const handleBlur = useCallback(() => {
      setIsFocus(false);

      if (onBlur) {
        onBlur();
      }
    }, [onBlur]);

    return (
      <Wrapper className={className} width={width}>
        <LabelWrapper hideLabel={hideLabel}>
          <Label htmlFor={id}>
            {label}
            {required && <Required />}
          </Label>
        </LabelWrapper>
        <InputWrapper
          hasError={hasError}
          height={height}
          isDisabled={disabled}
          isFocus={isFocus}
          type={type}
          width={width}
        >
          <WrapperSelect
            fontSize={fontSize}
            hasIcon
            lineHeight={lineHeight}
            size={size}
          >
            {options.find(
              (option) =>
                // eslint-disable-next-line eqeqeq
                option.value == value, // Don't ===, because input always return a string value
            )?.label ?? ' '}
            <StyledSelect
              aria-describedby={hasError ? errorId : undefined}
              aria-invalid={hasError}
              data-testid={dataTestId}
              disabled={disabled}
              id={id}
              onBlur={handleBlur}
              onChange={onChange}
              onFocus={handleFocus}
              ref={ref}
              value={value}
            >
              {options.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </StyledSelect>
          </WrapperSelect>

          <IconWrapper iconSpaceRight={iconSpaceRight} tabIndex={-1}>
            <IconArrowDownHollow size="100%" />
          </IconWrapper>
        </InputWrapper>
        {hasError && (
          <WrapperError id={errorId}>
            <IconExclamationMarkHollow height={ICON_SIZE} width={ICON_SIZE} />
            <Box ml={2} color="inherit">
              {error}
            </Box>
          </WrapperError>
        )}
      </Wrapper>
    );
  },
);

Select.propTypes = {
  id: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  options: PropTypes.array,
};

export default Select;

const getPadding = ({ theme, size }) => {
  switch (size) {
    case 'small':
      return css`
        padding: ${theme.space[2] - 1}px ${theme.space[3] - 1}px !important;
      `;

    case 'extra-small':
      return css`
        padding: ${theme.space[1]}px ${theme.space[2]}px !important;
      `;

    case 'normal':
    default:
      return css`
        padding: ${theme.space[3]}px !important;
      `;
  }
};

const getBoxShadow = ({ theme, hasError, isFocus }) => {
  if (isFocus) {
    return `${theme.input.focus.borderColor} 0 0 0 1px inset !important`;
  }

  if (hasError) {
    return `${theme.input.error.color} 0 0 0 1px inset !important`;
  }

  return `${theme.input.borderColor} 0 0 0 1px inset !important`;
};

const getInputStyle = ({ fontSize, theme }) => css`
  background: transparent !important;
  border-radius: 0 !important;
  border: 0 !important;
  color: ${theme.input.color} !important;
  font-family: 'Inter', 'Nunito', Helvetica, Arial, sans-serif !important;
  font-size: ${fontSize || theme.fontSizes[2]} !important;
  ${getPadding};
  width: 100%;
  padding-right: ${theme.space[3] * 2 + ICON_SIZE}px !important;

  &:focus {
    outline: none !important;
  }
`;

const Wrapper = styled(Box).attrs(() => ({
  display: 'block',
}))``;

const InputWrapper = styled(Box)
  .withConfig({
    shouldForwardProp: (prop) =>
      !['isFocus', 'hasError', 'type', 'isDisabled'].includes(prop),
  })
  .attrs(({ isDisabled, theme }) => ({
    opacity: isDisabled ? theme.input.disabled.opacity : 1,
    bg: isDisabled ? 'dove' : 'white',
  }))`
  border-radius: 3px;
  box-shadow: ${getBoxShadow};
  position: relative;
`;

const LabelWrapper = styled(Box)
  .withConfig({
    shouldForwardProp: (prop) => !['hideLabel'].includes(prop),
  })
  .attrs(() => ({
    mb: 2,
  }))`
  ${({ hideLabel }) =>
    hideLabel &&
    css`
      border: 0px !important;
      clip: rect(0 0 0 0) !important;
      clip-path: inset(100%) !important;
      height: 1px !important;
      overflow: hidden !important;
      padding: 0px !important;
      position: absolute !important;
      white-space: nowrap !important;
      width: 1px !important;
    `}
`;

export const Label = styled.label`
  color: ${({ theme }) => theme.input.color};
  cursor: ${({ htmlFor }) => (htmlFor ? 'pointer' : 'default')} !important;
  font-size: ${({ theme }) => theme.fontSizes[2]};
  font-weight: ${({ theme }) => theme.fontWeights[1]};
  user-select: none;
`;

const WrapperSelect = styled.div.withConfig({
  shouldForwardProp: (prop) =>
    !['fontSize', 'height', 'hasIcon', 'lineHeight', 'size'].includes(prop),
})`
  ${getInputStyle};

  cursor: pointer;
  line-height: ${({ lineHeight }) => lineHeight} !important;
  overflow: hidden;
  position: relative;
  text-overflow: ellipsis;
  white-space: pre;
`;

const StyledSelect = styled.select`
  appearance: none;
  background: transparent !important;
  border: none !important;
  bottom: 0;
  height: auto !important;
  left: 0;
  opacity: 0;
  padding: 0 !important;
  position: absolute;
  right: 0;
  top: 0;
  width: 100%;
`;

const WrapperError = styled(Box).attrs(() => ({
  alignItems: 'center',
  mt: 2,
}))`
  color: ${({ theme }) => theme.input.error.color};
`;

const IconWrapper = styled(Box)
  .withConfig({
    shouldForwardProp: (prop) => !['iconSpaceRight'].includes(prop),
  })
  .attrs(() => ({
    height: ICON_SIZE,
    width: ICON_SIZE,
  }))`
  ${focusOutline}

  color: ${({ theme }) => theme.input.focus.borderColor};
  cursor: default;
  margin-top: -${ICON_SIZE / 2}px;
  pointer-events: none;
  position: absolute;
  right: ${({ iconSpaceRight, theme }) => iconSpaceRight || theme.space[3]}px;
  top: 50%;
`;
